/**
 * Theme tokens — Spec 020 D1 (brand «Открыто»), replacing design spec §1.
 *
 * The colours are the brand's (`src/theme/brand.ts`); Telegram only tells us
 * which of the two themes to show. Every colour in the app still resolves
 * through CSS variables: components never name a colour, they name a token, so
 * one component tree renders correctly in both themes with no `dark:` variants.
 *
 * Applied once at boot and again on every `themeChanged` event, so a user
 * switching theme inside Telegram sees the app follow without a reload.
 */

import { BRAND, brandVariables, type ColorScheme } from "@/theme/brand";

export type { ColorScheme } from "@/theme/brand";

/** Accepts `#RGB` and `#RRGGBB`; anything else is treated as absent. */
function parseHex(value: string | undefined): [number, number, number] | null {
  if (!value) return null;
  const hex = value.trim().replace(/^#/, "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Relative luminance (WCAG). Used only to infer the scheme when the client
 * gives us colours but no `colorScheme` — a dark `bg_color` means dark theme.
 */
function luminance([r, g, b]: [number, number, number]): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * Decide which scheme we are in, most trustworthy source first:
 * explicit override → Telegram's own flag → the bg colour it sent → OS setting.
 */
export function resolveScheme(
  telegramScheme: ColorScheme | undefined,
  themeParams: Record<string, string> | null,
  override?: ColorScheme,
): ColorScheme {
  if (override) return override;
  if (telegramScheme === "light" || telegramScheme === "dark") return telegramScheme;
  const bg = parseHex(themeParams?.bg_color);
  if (bg) return luminance(bg) < 0.5 ? "dark" : "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Write the brand palette for `scheme` onto `:root` — the `--p-*` tokens and
 * the legacy names that point at them. `themeParams` no longer paints anything;
 * it is kept in the signature because `resolveScheme` may still read it.
 */
export function applyTheme(_themeParams: Record<string, string> | null, scheme: ColorScheme): void {
  const root = document.documentElement;
  for (const [name, value] of Object.entries(brandVariables(scheme))) {
    root.style.setProperty(name, value);
  }
  root.dataset.theme = scheme;
  // Keeps native widgets (scrollbars, date pickers, caret) in the same theme.
  root.style.colorScheme = scheme;
}

/** The colour Telegram should paint its own header and background with. */
export function chromeColor(scheme: ColorScheme): string {
  return BRAND[scheme].bg;
}
